import { FC, useMemo, useState } from 'react';

import { useLocale } from '../../../../../hooks/index';
import { stakeholderTypeTranslation } from '../../../../../translations/stakeholder-translation.util';
import {
  CapTableOrdering,
  Column,
  DetailsSortVariant,
  ShareClassColumns,
  ShareClassStats,
  StakeholderStats,
  ViewBy,
} from '../../../../../types/snapshot.types';
import { filterByAllFields } from '../../../../../utils/filter-by-all-fields.util';
import { toRound } from '../../../../../utils/get-rounded-number.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { formatDividendAmount } from '../../../../dividends/utils/format-dividend-amount.util';
import { CapTableExpandedRowsPanel } from '../../../components/cap-table-table-animations';
import { sortShareClassHoldersByOrdering } from '../../../utils/sort-cap-table-rows.util';
import { DEFAULT_CAP_TABLE_ORDERING } from '../../snapshot-view-by/snapshot-view-by.types';
import { TableRow } from '../table-row';
import { LoadMoreItem } from './load-more-item';
import { useCapTableGroupReveal } from './use-cap-table-group-reveal.hook';

type StakeholderTableItemProps = {
  className?: string;
  fields: Record<ShareClassColumns, null | number | string>;
  id?: string;
  isEquityPoolsGrantable?: boolean;
  /**
   * The Ordering tab's settings. Share class stays the primary grouping — this only decides how
   * the holders inside one class are laid out.
   */
  ordering?: CapTableOrdering;
  search?: string;
  stakeholders?: StakeholderStats[];
  viewBy: Record<ViewBy, boolean>;
  visibleColumns: Column[];
};

export const ShareClassTableItem: FC<StakeholderTableItemProps> = ({
  viewBy,
  visibleColumns,
  fields,
  stakeholders = [],
  className,
  id,
  ordering = DEFAULT_CAP_TABLE_ORDERING,
  search = '',
  isEquityPoolsGrantable = false,
}) => {
  const searchResult = useMemo(() => {
    const filtered = filterByAllFields(stakeholders, search);
    if (isEquityPoolsGrantable || !id) return filtered;

    return sortShareClassHoldersByOrdering(filtered, id, ordering);
  }, [id, isEquityPoolsGrantable, ordering, search, stakeholders]);

  const { messagesLocale } = useLocale();
  const [isExpanded, setExpanded] = useState(false);
  const [isExpandedVisual, setExpandedVisual] = useState(false);
  const {
    handleExtraCollapseComplete,
    handleLoadMore,
    handleShowLess,
    isExtraRowsOpen,
    loadCount,
    pinnedRowCount,
    renderedCount,
    showLess,
    showRevealControl,
  } = useCapTableGroupReveal(searchResult.length, isExpanded);

  const visibleStakeholders = searchResult.slice(0, renderedCount);

  const viewByAll = viewBy.stakeholders && viewBy.type;

  const isStakeholdersEmpty = stakeholders.length === 0;

  return (
    <>
      <TableRow
        disableExpand={isStakeholdersEmpty}
        fields={fields}
        handleExpandRow={setExpanded}
        isExpanded={isExpanded}
        isExpandedRow={false}
        isExpandedVisual={isExpandedVisual}
        tdClassName={className}
        type={DetailsSortVariant.BY_SHARE_CLASS}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
      {!isStakeholdersEmpty && (
        <CapTableExpandedRowsPanel
          footer={
            showRevealControl && (
              <LoadMoreItem
                loadCount={loadCount}
                onLoadMore={handleLoadMore}
                onShowLess={handleShowLess}
                showLess={showLess}
                viewByAll={Boolean(viewByAll)}
              />
            )
          }
          isExpanded={isExpanded}
          isExtraRowsOpen={isExtraRowsOpen}
          onExpandedVisualChange={setExpandedVisual}
          onExtraCollapseComplete={handleExtraCollapseComplete}
          pinnedRowCount={pinnedRowCount}
        >
          {isEquityPoolsGrantable
            ? visibleStakeholders.map(
                (
                  {
                    name,
                    diluted,
                    type,
                    dilutedPercentage,
                    issued,
                    issuedPercentage,
                    converted,
                    convertedPercentage,
                  },
                  i,
                ) => {
                  return (
                    <TableRow
                      fields={{
                        [Column.NAME]: name,
                        [Column.SHARE_CLASS_NAME]: null,
                        [Column.STAKEHOLDER_NAME]: name,
                        [Column.TYPE]: messagesLocale[stakeholderTypeTranslation[type]],
                        [Column.ISSUED]: toRound(issuedPercentage),
                        [Column.DILUTED]: toRound(dilutedPercentage),
                        [Column.VOTING]: null,
                        [Column.NO_ISSUED]: issued.toLocaleString('en-US'),
                        [Column.DILUTED_SHARES]: toNumber(diluted).toLocaleString('en-US'),
                        [Column.NO_OF_STAKEHOLDERS]: '-',
                        [Column.VOTING_RIGHTS]: null,
                        [Column.INVESTMENT]: null,
                        [Column.DIVIDENDS]: 0,
                        [Column.AVERAGE_SHARE_PRICE]: null,
                        [Column.CONVERTED_SHARES]: converted,
                        [Column.CONVERTED_PERCENTAGE]: toRound(convertedPercentage * 100),
                      }}
                      isExpandedRow={true}
                      isLastInExpandedGroup={
                        !showRevealControl && i === visibleStakeholders.length - 1
                      }
                      key={`${i}_${id}`}
                      type={DetailsSortVariant.BY_SHARE_CLASS}
                      viewBy={viewBy}
                      visibleColumns={visibleColumns}
                    />
                  );
                },
              )
            : visibleStakeholders.map(({ shareClasses, name, type }, i) => {
                const stakeholderShareClass =
                  shareClasses.find((shareClass) => shareClass.id === id) ||
                  ({} as Partial<ShareClassStats>);

                const {
                  diluted,
                  dilutedPercentage,
                  issued,
                  issuedPercentage,
                  votingPercentage,
                  votingRight,
                  investment,
                  dividends,
                  avgSharePrice,
                  converted,
                  convertedPercentage,
                } = stakeholderShareClass;

                return (
                  <TableRow
                    fields={{
                      [Column.NAME]: name,
                      [Column.SHARE_CLASS_NAME]: null,
                      [Column.STAKEHOLDER_NAME]: name,
                      [Column.TYPE]: messagesLocale[stakeholderTypeTranslation[type]],
                      [Column.ISSUED]: toRound(toNumber(issuedPercentage) * 100, 2),
                      [Column.DILUTED]: toRound(toNumber(dilutedPercentage) * 100, 2),
                      [Column.VOTING]: toRound(toNumber(votingPercentage) * 100),
                      [Column.NO_ISSUED]: toNumber(issued).toLocaleString('en-US'),
                      [Column.DILUTED_SHARES]: toNumber(diluted).toLocaleString('en-US'),
                      [Column.NO_OF_STAKEHOLDERS]: 0,
                      [Column.VOTING_RIGHTS]: toNumber(votingRight).toLocaleString('en-US'),
                      [Column.INVESTMENT]: toNumber(investment).toLocaleString('en-US'),
                      [Column.DIVIDENDS]: formatDividendAmount(toNumber(dividends)),
                      [Column.AVERAGE_SHARE_PRICE]: toRound(avgSharePrice),
                      [Column.CONVERTED_SHARES]: toNumber(converted),
                      [Column.CONVERTED_PERCENTAGE]: `${toRound(toNumber(convertedPercentage) * 100)}`,
                    }}
                    isExpandedRow={true}
                    isLastInExpandedGroup={
                      !showRevealControl && i === visibleStakeholders.length - 1
                    }
                    key={`${i}_${id}`}
                    type={DetailsSortVariant.BY_SHARE_CLASS}
                    viewBy={viewBy}
                    visibleColumns={visibleColumns}
                  />
                );
              })}
        </CapTableExpandedRowsPanel>
      )}
    </>
  );
};
